import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AiOutlineLoading } from "react-icons/ai";
import { GoAlertFill } from "react-icons/go";
import { Button } from "flowbite-react";
import useLogin from "../hooks/useLogin";
import { checkIfAuthenticated } from "../utils/utils";
import { LOGIN } from "../config/apiConfig";
import { motion } from "framer-motion";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa6";
import { MdOutlineGavel } from "react-icons/md";
import { RiBankCardLine, RiMoneyDollarCircleLine } from "react-icons/ri";
import {
  HiOutlineOfficeBuilding,
  HiShieldCheck,
  HiChartSquareBar,
  HiGlobeAlt,
  HiRefresh,
  HiCheckCircle,
} from "react-icons/hi";

const FEATURES = [
  {
    title: "Protesto Nacional",
    desc: "Cobertura cartorial ampla",
    icon: MdOutlineGavel,
  },
  {
    title: "Pendências Financeiras",
    desc: "PEFIN, REFIN e mais",
    icon: RiMoneyDollarCircleLine,
  },
  {
    title: "Dados da Receita",
    desc: "CPF/CNPJ, sócios e situação",
    icon: HiOutlineOfficeBuilding,
  },
  {
    title: "Score & Risco",
    desc: "Prob. de atraso e limites",
    icon: HiChartSquareBar,
  },
  {
    title: "Consultas Judiciais",
    desc: "Ações cíveis e trabalhistas",
    icon: HiShieldCheck,
  },
  {
    title: "Cheques sem Fundo",
    desc: "CCF e ocorrências bancárias",
    icon: RiBankCardLine,
  },
];

const chipVariants = {
  hidden: { opacity: 0, x: 16 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { delay: 0.12 + i * 0.07, duration: 0.4, ease: "easeOut" },
  }),
};

export default function LoginPage() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (checkIfAuthenticated()) navigate("/dashboard", { replace: true });
  }, [navigate]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { loading, error, isLoggedIn, login } = useLogin(LOGIN.POST_LOGIN);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const recaptchaToken = await window.grecaptcha.execute(
        "6LeEPpMrAAAAACiEWFXzEAzAulmcTy78Xf5o4VFX",
        { action: "login" }
      );

      await login(email, password, recaptchaToken);

      window.location.replace("/dashboard");
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (isLoggedIn || checkIfAuthenticated()) {
      navigate("/dashboard", { replace: true });
    }
  }, [isLoggedIn, navigate]);
  useEffect(() => {
    if (isLoggedIn) navigate("/dashboard");
  }, [isLoggedIn, navigate]);

  let logoSrc;

  switch (true) {
    case window.location.href.includes("avaliecreditoconsultas"):
      logoSrc = "/assets/white-label/avalie-credito/logo.png";
      break;
    case window.location.href.includes("acessopositivo"):
      logoSrc = "/assets/white-label/acesso-positivo/logo-azul.png";
      break;
    case window.location.href.includes("proconsultbr"):
      logoSrc = "/assets/white-label/pro-consult-br/logo_consult.png";
      break;
    case window.location.href.includes("simscore"):
      logoSrc = "/assets/white-label/sim-pesquisa/logo_sim.png";
      break;
    case window.location.href.includes("simpesquisa"):
      logoSrc = "/assets/white-label/sim-pesquisa/logo_sim.png";
      break;
    case window.location.href.includes("credoresbrasil"):
      logoSrc = "/assets/white-label/credores-brasil/logo.png";
      break;
    case window.location.href.includes("consultaspositivo"):
      logoSrc = "/assets/white-label/consultas-positivo/logo.png";
      break;
    case window.location.href.includes("raioxconsultas"):
      logoSrc = "/assets/white-label/raio-x/logo.png";
      break;
    case window.location.href.includes("obahcredito"):
      logoSrc = "/assets/white-label/obah-credito/logo.png";
      break;
    case window.location.href.includes("hellusconsultoria"):
      logoSrc = "/assets/white-label/hellus-consultoria/logo.png";
      break;
    default:
      logoSrc = "/assets/relinq_1.png";
      break;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* 50/50 em md+: duas colunas iguais */}
      <div className="grid grid-cols-1 md:grid-cols-2">
        {/* Coluna ESQUERDA (50%) — Form */}
        <div className="min-h-screen flex items-start md:items-center justify-center px-6 md:px-10 py-8 relative">
          <div className="w-full max-w-lg">
            {/* Header */}
            <div className="mb-2">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-white px-3 py-1 text-xs text-cyan-700">
                <span className="inline-block h-2 w-2 rounded-full bg-cyan-500"></span>
                Ambiente seguro
              </div>
              <h1 className="mt-1 text-2xl md:text-4xl font-semibold text-gray-900 tracking-tight">
                Bem-vindo(a) de volta!
              </h1>
              <p className="text-gray-600 md:mt-1">
                Entre para acessar suas consultas e análises.
              </p>
            </div>

            {/* Card com moldura gradiente */}
            <div className="rounded-2xl bg-gradient-to-br from-cyan-200/70 via-sky-200/60 to-indigo-200/50 p-[1.2px] shadow-lg">
              <div className="rounded-2xl bg-white p-6 md:p-8">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      E-mail ou Código de acesso
                    </label>
                    <input
                      id="email"
                      type="text"
                      className="w-full rounded-xl bg-white text-gray-900 placeholder-gray-400 border border-gray-200 p-3 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      placeholder="exemplo@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="username"
                      required
                    />
                  </div>

                  <div className="relative">
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Senha
                    </label>
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      className="w-full rounded-xl bg-white text-gray-900 placeholder-gray-400 border border-gray-200 p-3 pr-12 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-10 text-gray-500 hover:text-gray-700"
                      aria-label={
                        showPassword ? "Esconder senha" : "Mostrar senha"
                      }
                    >
                      {!showPassword ? <FaRegEye /> : <FaRegEyeSlash />}
                    </button>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                      <GoAlertFill className="text-red-500" size={18} />
                      {error?.response?.data?.message ||
                        "Erro ao tentar fazer login."}
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full text-white bg-cyan-600 py-2 hover:bg-cyan-500 rounded-xl transition-all duration-200 font-semibold"
                    isProcessing={loading}
                    processingSpinner={
                      <AiOutlineLoading className="h-5 w-5 animate-spin" />
                    }
                    disabled={loading}
                  >
                    Entrar
                  </Button>

                  {/* <div className="text-sm text-right">
                    <Link to="/recuperar-senha" className="text-cyan-700 hover:underline">Esqueci minha senha</Link>
                  </div> */}
                </form>

                <p className="text-[10px] text-center text-gray-500 mt-6">
                  Este site é protegido pelo reCAPTCHA
                </p>
              </div>
            </div>

            {/* Chips abaixo */}
            <div className="mt-4 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-blue-200 text-blue-700 text-xs shadow-sm bg-blue-50">
                <HiGlobeAlt className="text-blue-600" />
                Cobertura nacional
              </span>

              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-50 border border-green-200 text-green-700 text-xs shadow-sm">
                <HiRefresh className="text-green-600" />
                Dados atualizados
              </span>

              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-700 text-xs shadow-sm">
                <HiCheckCircle className="text-cyan-600" />
                Decisões assertivas
              </span>
            </div>
          </div>
        </div>

        {/* Coluna DIREITA (50%) — Vitrine full-height */}
        <div className="relative min-h-[60vh] md:min-h-screen overflow-hidden">
          {/* Imagem base */}
          <img
            src="/assets/bg-login-2.jpg"
            alt="Vitrine de análise de crédito"
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Máscara diagonal clara */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(135deg, rgba(134, 225, 255, 0.88) 0%, rgba(255,255,255,0.55) 40%, rgba(255,255,255,0.25) 100%)",
              backdropFilter: "saturate(120%) blur(4px)",
            }}
          />

          {/* Blobs sutis */}
          <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-cyan-300/30 blur-3xl" />
          <div className="absolute right-10 bottom-10 h-40 w-40 rounded-full bg-sky-300/30 blur-2xl" />

          {/* Conteúdo */}
          <div className="relative z-10 min-h-screen flex flex-col justify-center">
            {/* Headline */}
            <motion.div
              className="px-16 pt-10 md:pt-16"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
            >
              <h2 className="text-gray-900 text-2xl md:text-3xl font-semibold">
                Decisões de crédito confiáveis
              </h2>
              <p className="text-gray-700 mt-2 max-w-2xl">
                O melhor sistema de consultas do mercado.
              </p>
            </motion.div>

            {/* Rail de features */}
            <div className="mt-8 md:mt-5 px-16 relative max-w-3xl">
              <div className="inset-0 flex items-center">
                <div className="mr-6 md:mr-12 w-full">
                  <ul className="gap-3 grid grid-cols-2">
                    {FEATURES.map((f, i) => {
                      const Icon = f.icon;
                      return (
                        <motion.li
                          key={f.title}
                          custom={i}
                          variants={chipVariants}
                          initial="hidden"
                          animate="visible"
                          className="group rounded-2xl bg-white/90 backdrop-blur-md border border-gray-200 shadow-sm hover:shadow-md transition-all"
                        >
                          <div className="flex items-center gap-3 p-4">
                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-200 bg-cyan-50 text-cyan-700">
                              <Icon size={20} />
                            </span>
                            <div className="min-w-0">
                              <div className="text-gray-900 font-medium truncate">
                                {f.title}
                              </div>
                              <div className="text-gray-600 text-sm truncate">
                                {f.desc}
                              </div>
                            </div>
                          </div>
                        </motion.li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
